import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, Stack, Paper, LinearProgress, IconButton,
  FormControl, InputLabel, Select, MenuItem, TextField, Tabs, Tab, Table, Divider,
  TableHead, TableBody, TableRow, TableCell, Tooltip, Alert,
} from '@mui/material';
import {
  Shield as ShieldIcon, Public as PublicIcon, People as PeopleIcon,
  FavoriteBorder as LikeIcon, BookmarkBorder as SaveIcon, OutlinedFlag as FlagIcon,
  Lock as LockIcon, OpenInNew as ExternalIcon, ArrowBack as BackIcon,
  ArrowForward as NextIcon, Close as CloseIcon, Bolt as AIIcon,
  Schedule as ManualIcon, AutoAwesome as SparkIcon, CheckCircle as DoneIcon,
} from '@mui/icons-material';

// TrustSafetyDemo - simulated end-to-end abuse-reporting walkthrough.
// Faithfully recreates real Percoro surfaces (Community card, ReportContentModal,
// TrustSafetyTab queue). NO API calls, NO real data. Two variants via ?v=advisory.

const TEAL = '#2D6E6A';
const TEAL_DARK = '#1F4E4A';
const PURPLE = '#574B90';
const INK = '#1a1a2e';
const GOLD = '#FFD700';

const PERCORO_URL = 'https://percoro.com';
const CHAADV_CONTACT = 'https://chaadv.com/contact?source=trust-safety-demo';
const NCMEC_URL = 'https://report.cybertip.org/';

type Variant = 'percoro' | 'advisory';

const REASONS = [
  'Child sexual abuse or exploitation',
  'Nudity or sexual content',
  'Harassment or bullying',
  'Hate speech',
  'Violence or threats',
  'Illegal content or activity',
  'Spam or scam',
  'Something else',
];

const CommunityCard: React.FC<{
  flagPulse?: boolean;
  onFlag?: () => void;
  reported?: boolean;
}> = ({ flagPulse, onFlag, reported }) => {
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  return (
    <Box>
      <Tabs value={0} sx={{ mb: 1.5, minHeight: 36, '& .MuiTabs-indicator': { display: 'none' } }}>
        <Tab icon={<PublicIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Discover"
          sx={{ minHeight: 36, textTransform: 'none', borderRadius: 2, mr: 1, bgcolor: 'rgba(45,110,106,0.1)', color: TEAL }} />
        <Tab icon={<PeopleIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Friends"
          sx={{ minHeight: 36, textTransform: 'none', color: '#65726f' }} />
      </Tabs>
      <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid #d8e0df' }}>
        <Box sx={{
          height: 200,
          background: 'linear-gradient(135deg, #cfdAD8 0%, #aebfbc 45%, #c2b9a8 100%)',
          filter: 'blur(10px)',
          transform: 'scale(1.1)',
        }} />
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Typography variant="caption" sx={{ color: '#5f6d6a', fontWeight: 600, bgcolor: 'rgba(255,255,255,0.55)', px: 1, py: 0.25, borderRadius: 1 }}>Shared trip photo &middot; public</Typography>
        </Box>
        {reported && (
          <Box sx={{ position: 'absolute', top: 8, left: 8, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', px: 1, py: 0.25, borderRadius: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DoneIcon sx={{ fontSize: 14 }} /><Typography variant="caption">Report submitted</Typography>
          </Box>
        )}
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.55))',
          display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.75,
        }}>
          <IconButton size="small" onClick={() => setLiked((v) => !v)} sx={{ color: liked ? '#ff6b81' : '#fff' }}>
            <LikeIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ color: '#fff', mr: 1 }}>{liked ? 13 : 12}</Typography>
          <IconButton size="small" onClick={() => setSaved((v) => !v)} sx={{ color: saved ? GOLD : '#fff' }}>
            <SaveIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ color: '#fff', mr: 1 }}>{saved ? 4 : 3}</Typography>
          <Box sx={{ flex: 1 }} />
          <Box sx={{
            borderRadius: '50%',
            border: flagPulse && !reported ? '2px solid ' + GOLD : '2px solid transparent',
            boxShadow: flagPulse && !reported ? '0 0 0 4px rgba(255,215,0,0.25)' : 'none',
            transition: 'all 0.2s',
          }}>
            <Tooltip title="Report this photo" open={flagPulse && !reported} arrow placement="top">
              <IconButton size="small" onClick={onFlag} sx={{ color: (flagPulse && !reported) ? GOLD : '#fff' }}>
                <FlagIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      <Typography variant="caption" sx={{ color: '#9aa6a4', display: 'block', mt: 1 }}>
        On Discover, anyone sees public photos. On Friends, content is visible only to connected friends &mdash; by design.
      </Typography>
    </Box>
  );
};

const ReportDialog: React.FC<{
  onSubmit?: (reason: string) => void;
  onCancel?: () => void;
  embedded?: boolean;
}> = ({ onSubmit, onCancel, embedded }) => {
  const [reason, setReason] = useState('Illegal content or activity');
  const [details, setDetails] = useState('');
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: embedded ? '0 8px 30px rgba(0,0,0,0.18)' : 'none' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #ececec' }}>
        <Typography variant="subtitle1" fontWeight={600}>Report this photo</Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tell us what's wrong. Reports are confidential &mdash; the person who posted this won't see that you reported it.
        </Typography>
        <FormControl size="small" fullWidth sx={{ mb: 2 }}>
          <InputLabel shrink>Reason</InputLabel>
          <Select label="Reason" value={reason} notched onChange={(e) => setReason(e.target.value)}>
            {REASONS.map((r) => <MenuItem key={r} value={r} disabled={r !== 'Illegal content or activity'}>{r}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField label="Details (optional)" multiline minRows={2} fullWidth size="small"
          placeholder="Add anything that helps us review this"
          value={details} onChange={(e) => setDetails(e.target.value)} />
      </Box>
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #ececec', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button size="small" onClick={onCancel} sx={{ textTransform: 'none', color: '#65726f' }}>Cancel</Button>
        <Button size="small" variant="contained" onClick={() => onSubmit && onSubmit(reason)}
          sx={{ bgcolor: TEAL, textTransform: 'none', '&:hover': { bgcolor: TEAL_DARK } }}>
          Submit report
        </Button>
      </Box>
    </Paper>
  );
};

// Step 1 container: community card + flag -> overlay report dialog -> submit -> reported state.
const Step1Community: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reported, setReported] = useState(false);
  return (
    <Box sx={{ position: 'relative' }}>
      <CommunityCard flagPulse={!reported && !dialogOpen} reported={reported} onFlag={() => setDialogOpen(true)} />
      {dialogOpen && (
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, zIndex: 5, borderRadius: 2 }}>
          <Box sx={{ width: '100%', maxWidth: 360 }}>
            <ReportDialog embedded
              onCancel={() => setDialogOpen(false)}
              onSubmit={() => { setDialogOpen(false); setReported(true); }} />
          </Box>
        </Box>
      )}
      {reported && (
        <Alert severity="success" sx={{ mt: 1.5, '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
          Report submitted. The photo stays visible to you, but it's now in the review queue — the poster never sees that a User reported it.
        </Alert>
      )}
    </Box>
  );
};

const ModerationQueue: React.FC = () => {
  const [reports, setReports] = useState<any[]>([
    { id: 'rpt-1', reason: 'illegal', reasonLabel: 'Illegal content', content: 'photo', contentId: 'a3f9c2e1', status: 'open', acked: false, hold: true, ncmec: '', reported: 'Jun 2, 2026', log: [] },
    { id: 'rpt-2', reason: 'illegal', reasonLabel: 'Illegal content', content: 'photo', contentId: 'b7d1e882', status: 'open', acked: false, hold: true, ncmec: '', reported: 'Jun 2, 2026', log: [] },
    { id: 'rpt-3', reason: 'csam', reasonLabel: 'Child sexual abuse (CSAM)', content: 'photo', contentId: 'c1a4f730', status: 'open', acked: false, hold: true, ncmec: 'NCMEC-4471829', reported: 'Jun 2, 2026', log: [{ action: 'Reported to NCMEC', when: 'Jun 2, 1:02 PM', who: 'system', note: 'ref NCMEC-4471829' }] },
  ]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [releasedFor, setReleasedFor] = useState<string | null>(null);
  const [ncmecRef, setNcmecRef] = useState('');
  const [note, setNote] = useState('');
  const [editStatus, setEditStatus] = useState('open');

  const STATUS_OPTIONS = ['open', 'reviewing', 'actioned', 'dismissed'];
  const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);
  const open = openId ? reports.find((r) => r.id === openId) : null;

  const openDetail = (r: any) => { setOpenId(r.id); setEditStatus(r.status); setNcmecRef(''); setNote(''); setReleasedFor(null); };
  const patch = (fn: (r: any) => any, logEntry?: any) => {
    if (!openId) return;
    setReports((prev) => prev.map((r) => {
      if (r.id !== openId) return r;
      const next = fn({ ...r });
      if (logEntry) next.log = [...r.log, { ...logEntry, when: 'Just now', who: 'alex.morgan' }];
      return next;
    }));
  };
  const acknowledge = () => patch((r) => { r.acked = true; if (r.status === 'open') r.status = 'reviewing'; return r; }, { action: 'Acknowledged', note: '' });
  const contain = () => patch((r) => { r.hold = true; return r; }, { action: 'Content contained', note: '' });
  const release = () => {
    const n = note.trim();
    if (!n) { setNote('Reviewed the content reference. Not a policy violation — releasing the photo back to the community.'); return; }
    patch((r) => { r.hold = false; if (r.status === 'open') r.status = 'reviewing'; return r; }, { action: 'Content released', note: n });
    setReleasedFor(openId);
    setNote('');
  };
  const recordNcmec = () => { const ref = ncmecRef.trim(); if (!ref) return; patch((r) => { r.ncmec = ref; return r; }, { action: 'Reported to NCMEC', note: 'ref ' + ref }); setNcmecRef(''); };
  const addNote = () => { const n = note.trim(); if (!n) return; patch((r) => r, { action: 'Note added', note: n }); setNote(''); };
  const setStatus = () => patch((r) => { r.status = editStatus; return r; }, { action: 'Status changed', note: editStatus });

  const unacked = reports.filter((r) => !r.acked).length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ShieldIcon sx={{ color: TEAL }} />
        <Typography variant="h6" fontWeight={600}>Trust &amp; Safety</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        High-priority escalation queue. Child-safety (CSAM) and illegal-content reports are preserved and contained automatically on receipt. The reported material is never displayed here &mdash; it is referenced by ID only.
      </Typography>
      {unacked > 0 && (
        <Alert severity="error" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '0.82rem' } }}>
          {unacked} high-priority report{unacked > 1 ? 's' : ''} awaiting acknowledgment.
        </Alert>
      )}
      <Paper variant="outlined" sx={{ overflow: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.72rem', color: '#65726f' } }}>
            <TableCell>Reason</TableCell><TableCell>Content</TableCell><TableCell>Status</TableCell>
            <TableCell>Ack</TableCell><TableCell>Hold</TableCell><TableCell>NCMEC</TableCell><TableCell align="right">Action</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {reports.map((r) => (
              <TableRow key={r.id} sx={!r.acked ? { bgcolor: 'rgba(211,47,47,0.06)' } : undefined}>
                <TableCell><Chip size="small" color="error" variant={r.reason === 'csam' ? 'filled' : 'outlined'} label={r.reasonLabel} sx={{ maxWidth: 200 }} /></TableCell>
                <TableCell><Typography variant="body2">{r.content}</Typography><Typography variant="caption" color="text.secondary">{r.contentId}…</Typography></TableCell>
                <TableCell><Chip size="small" label={r.status} /></TableCell>
                <TableCell>{r.acked ? <Chip size="small" color="success" label="Yes" /> : <Chip size="small" color="warning" label="No" />}</TableCell>
                <TableCell>{r.hold ? <Tooltip title="Evidence preserved; account cannot be purged"><LockIcon fontSize="small" sx={{ color: TEAL }} /></Tooltip> : <Typography variant="caption" sx={{ color: '#9aa6a4' }}>&mdash;</Typography>}</TableCell>
                <TableCell>{r.ncmec ? <Chip size="small" color="success" label="Filed" /> : <Typography variant="caption" sx={{ color: '#9aa6a4' }}>&mdash;</Typography>}</TableCell>
                <TableCell align="right"><Button size="small" variant="contained" onClick={() => openDetail(r)} sx={{ bgcolor: TEAL, textTransform: 'none', fontSize: '0.7rem', py: 0.25, '&:hover': { bgcolor: TEAL_DARK } }}>Review</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {open && (
        <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <Paper sx={{ width: '100%', maxWidth: 640, maxHeight: '88vh', overflowY: 'auto', borderRadius: 2 }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #ececec', display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShieldIcon sx={{ color: TEAL }} />
              <Typography variant="h6" fontWeight={600}>Escalation runbook</Typography>
              <Box sx={{ flex: 1 }} />
              <IconButton size="small" onClick={() => setOpenId(null)}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            <Box sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <Alert severity="warning" icon={<LockIcon />} sx={{ '& .MuiAlert-message': { fontSize: '0.82rem' } }}>
                  Do not download, copy, save, or forward the reported material. Work from the content reference only. Report it to NCMEC and let them retrieve it.
                </Alert>
                <Box>
                  <Typography variant="caption" color="text.secondary">Report</Typography>
                  <Typography variant="body2">{open.reasonLabel} &middot; {open.content} &middot; {open.contentId}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Filed {open.reported}{open.acked ? ' \u00b7 acknowledged' : ' \u00b7 not yet acknowledged'}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Reporter: Anonymous</Typography>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>1. Acknowledge</Typography>
                  <Typography variant="caption" color="text.secondary">Confirm a human has picked this up. Logged to the chain of custody.</Typography>
                  <Box sx={{ mt: 1 }}>
                    <Button size="small" variant="contained" disabled={open.acked} onClick={acknowledge} sx={{ bgcolor: TEAL, textTransform: 'none', '&:hover': { bgcolor: TEAL_DARK } }}>{open.acked ? 'Acknowledged' : 'Acknowledge'}</Button>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>2. Contain content</Typography>
                  <Typography variant="caption" color="text.secondary">Hide the photo from all feeds (auto-applied on a high-priority report; re-apply or release here).</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" variant="contained" onClick={contain} sx={{ bgcolor: TEAL, textTransform: 'none', '&:hover': { bgcolor: TEAL_DARK } }}>Contain</Button>
                    <Button size="small" variant="outlined" onClick={release} sx={{ color: TEAL, borderColor: TEAL, textTransform: 'none' }}>Release</Button>
                    {!note.trim() && <Typography variant="caption" sx={{ color: '#b06a3b', display: 'block', mt: 0.5 }}>Release requires a documented note — click Release to prefill one, then confirm.</Typography>}
                  </Stack>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>3. Report to NCMEC</Typography>
                  <Stack spacing={0.5} sx={{ mb: 1 }}>
                    <Button variant="text" size="small" endIcon={<ExternalIcon fontSize="small" />} href={NCMEC_URL} target="_blank" rel="noopener noreferrer" sx={{ color: TEAL, textTransform: 'none', justifyContent: 'flex-start', px: 0 }}>File a CyberTipline report</Button>
                    <Typography variant="caption" color="text.secondary">24/7 hotline: 1-800-THE-LOST (1-800-843-5678)</Typography>
                  </Stack>
                  {open.ncmec ? (
                    <Alert severity="success" sx={{ '& .MuiAlert-message': { fontSize: '0.8rem' } }}>Filed &middot; report # {open.ncmec}</Alert>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField size="small" label="NCMEC report number" value={ncmecRef} onChange={(e) => setNcmecRef(e.target.value)} sx={{ flex: 1 }} />
                      <Button size="small" variant="contained" disabled={!ncmecRef.trim()} onClick={recordNcmec} sx={{ bgcolor: TEAL, textTransform: 'none', '&:hover': { bgcolor: TEAL_DARK } }}>Record</Button>
                    </Stack>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>4. Document &amp; resolve</Typography>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    <TextField size="small" label="Add a note (logged)" multiline minRows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                    <Button size="small" variant="outlined" disabled={!note.trim()} onClick={addNote} sx={{ color: TEAL, borderColor: TEAL, textTransform: 'none', alignSelf: 'flex-start' }}>Add note</Button>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                          {STATUS_OPTIONS.map((st) => <MenuItem key={st} value={st}>{cap(st)}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <Button size="small" variant="contained" disabled={editStatus === open.status} onClick={setStatus} sx={{ bgcolor: TEAL, textTransform: 'none', '&:hover': { bgcolor: TEAL_DARK } }}>Set status</Button>
                    </Stack>
                  </Stack>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Chain of custody</Typography>
                  {open.log.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No actions logged yet.</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {open.log.map((a: any, i: number) => (
                        <Box key={i} sx={{ borderLeft: '3px solid ' + TEAL, pl: 1.5, py: 0.25 }}>
                          <Typography variant="body2" fontWeight={600}>{a.action}</Typography>
                          <Typography variant="caption" color="text.secondary">{a.when} &middot; {a.who}</Typography>
                          {a.note && <Typography variant="body2" sx={{ mt: 0.25 }}>{a.note}</Typography>}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Box>
            <Box sx={{ px: 3, py: 2, borderTop: '1px solid #ececec', display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => setOpenId(null)} sx={{ textTransform: 'none' }}>Close</Button>
            </Box>
          </Paper>
        </Box>
      )}
      {releasedFor && (
        <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.4)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <Paper sx={{ width: '100%', maxWidth: 420, borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #ececec', display: 'flex', alignItems: 'center', gap: 1 }}>
              <DoneIcon sx={{ color: TEAL }} />
              <Typography variant="h6" fontWeight={600}>Photo restored to community</Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid #d8e0df', mb: 2 }}>
                <Box sx={{ height: 150, background: 'linear-gradient(135deg, #cfdAD8 0%, #aebfbc 45%, #c2b9a8 100%)', filter: 'blur(10px)', transform: 'scale(1.1)' }} />
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                  <Typography variant="caption" sx={{ color: '#5f6d6a', fontWeight: 600, bgcolor: 'rgba(255,255,255,0.55)', px: 1, py: 0.25, borderRadius: 1 }}>Shared trip photo &middot; public</Typography>
                </Box>
              </Box>
              <Alert severity="success" sx={{ '& .MuiAlert-message': { fontSize: '0.82rem' } }}>
                The photo is now restored and visible on the Community page again — the review found it was not a policy violation. The release and your note are recorded on the case's chain of custody.
              </Alert>
            </Box>
            <Box sx={{ px: 3, py: 2, borderTop: '1px solid #ececec', display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={() => { setReleasedFor(null); setOpenId(null); }} sx={{ bgcolor: TEAL, textTransform: 'none', '&:hover': { bgcolor: TEAL_DARK } }}>Done</Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

// Step 4 container: admin dashboard (only Trust & Safety enabled) -> click -> queue.
const ADMIN_DASH_CARDS = [
  { key: 'safety', title: 'Safety Reports', value: '3', caption: '3 high-priority open', enabled: true },
  { key: 'support', title: 'User Support', value: '0', caption: 'No open tickets', enabled: false },
  { key: 'system', title: 'System Health', value: 'Operational', caption: 'All checks passing', enabled: false },
  { key: 'ai', title: 'AI Warnings', value: '0.0%', caption: 'Error rate nominal', enabled: false },
  { key: 'mod', title: 'Moderation', value: '0', caption: 'Queue clear', enabled: false },
  { key: 'access', title: 'Access Requests', value: '0', caption: 'No pending requests', enabled: false },
];
const Step4Admin: React.FC = () => {
  const [view, setView] = useState<'dash' | 'queue'>('dash');
  if (view === 'queue') {
    return (
      <Box>
        <Button onClick={() => setView('dash')} startIcon={<BackIcon />} sx={{ textTransform: 'none', color: TEAL, mb: 1 }}>Back to dashboard</Button>
        <ModerationQueue />
      </Box>
    );
  }
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ color: INK, mb: 0.5 }}>What needs your attention</Typography>
      <Typography variant="caption" sx={{ color: '#65726f', display: 'block', mb: 1.5 }}>A live snapshot of safety, support, and system health. Only Trust &amp; Safety is interactive in this demo &mdash; click it to work the queue.</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
        {ADMIN_DASH_CARDS.map((c) => (
          <Paper key={c.key} variant="outlined"
            onClick={c.enabled ? () => setView('queue') : undefined}
            sx={{
              p: 1.5, borderRadius: 2, cursor: c.enabled ? 'pointer' : 'not-allowed',
              borderColor: c.enabled ? TEAL : '#e4e8e7', opacity: c.enabled ? 1 : 0.55,
              transition: 'all 0.15s',
              '&:hover': c.enabled ? { transform: 'translateY(-2px)', boxShadow: '0 6px 18px rgba(0,0,0,0.1)' } : {},
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <ShieldIcon sx={{ fontSize: 16, color: c.enabled ? TEAL : '#9aa6a4' }} />
              <Typography variant="caption" sx={{ color: '#65726f', fontWeight: 600 }}>{c.title}</Typography>
            </Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: c.enabled ? TEAL : '#9aa6a4', lineHeight: 1.1 }}>{c.value}</Typography>
            <Typography variant="caption" sx={{ color: '#9aa6a4' }}>{c.caption}</Typography>
            {c.enabled && <Typography variant="caption" sx={{ color: TEAL, fontWeight: 700, display: 'block', mt: 0.5 }}>Open Trust &amp; Safety &rarr;</Typography>}
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

interface FlowStep {
  icon: React.ReactNode;
  title: string;
  narration: string;
  ux: React.ReactNode;
  rigor: { title: string; body: string };
  manual: string;
  ai: string;
  tags: string[];
}

const STEPS: FlowStep[] = [
  {
    icon: <FlagIcon />,
    title: 'User flags a photo',
    narration: 'On the Community page, every shared photo carries three controls \u2014 like, save, and report. Anyone can flag content, signed in or anonymously.',
    ux: <Step1Community />,
    rigor: { title: 'Reporting is one tap', body: 'The flag sits right on the photo, next to like and save. Lowering the barrier to report is itself a safety control \u2014 and logged-out visitors can still report illegal content they can see.' },
    manual: 'A user has to find an email address or a form buried in a help page \u2014 most never bother.',
    ai: 'The report control is on the content itself. One tap opens the flow, with the exact photo already attached.',
    tags: ['Notice-and-action', 'Accessibility'],
  },

  {
    icon: <ShieldIcon />,
    title: 'High-severity content is removed automatically',
    narration: 'For child-safety and illegal-content reports, the photo comes down on its own \u2014 no admin needed, no message posted anywhere.',
    ux: (
      <Stack spacing={1.5}>
        <Box>
          <Tabs value={0} sx={{ mb: 1.5, minHeight: 36, '& .MuiTabs-indicator': { display: 'none' } }}>
            <Tab icon={<PublicIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Discover"
              sx={{ minHeight: 36, textTransform: 'none', borderRadius: 2, mr: 1, bgcolor: 'rgba(45,110,106,0.1)', color: TEAL }} />
            <Tab icon={<PeopleIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Friends"
              sx={{ minHeight: 36, textTransform: 'none', color: '#65726f' }} />
          </Tabs>
          <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid #d8e0df' }}>
            <Box sx={{ height: 200, bgcolor: INK, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <ShieldIcon sx={{ color: '#fff', fontSize: 34 }} />
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>Photo removed from all feeds</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>No notice posted &middot; evidence preserved</Typography>
            </Box>
          </Box>
          <Typography variant="caption" sx={{ color: '#9aa6a4', display: 'block', mt: 1 }}>
            The same Community card &mdash; the reported photo is now gone from Discover for everyone, automatically.
          </Typography>
        </Box>
        <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
          We build the auto-removal rules: which reasons trigger it, how fast, and what gets preserved. The system enforces them every time.
        </Alert>
      </Stack>
    ),
    rigor: { title: 'Rules we author, enforced automatically', body: 'Severity is decided server-side, then a single indexed flag pulls the photo from every public feed in milliseconds and a preservation hold blocks deletion. The rules are explicit and ours to tune \u2014 not a moderator\u2019s in-the-moment guess.' },
    manual: 'Someone must notice, decide it\u2019s serious, manually hide it, and remember not to delete the evidence \u2014 under time pressure.',
    ai: 'Defined rules fire the instant the report lands. The content is gone before a human opens the queue, and the evidence is locked.',
    tags: ['Auto-removal rules', 'Chain-of-custody', 'Evidence preservation'],
  },
  {
    icon: <ShieldIcon />,
    title: 'An admin works the queue',
    narration: 'High-priority reports land in the escalation queue. A reviewer acknowledges, contains, and documents \u2014 every step on an append-only record.',
    ux: <Step4Admin />,
    rigor: { title: 'A runbook, not improvisation', body: 'Each report carries the same four-step runbook \u2014 acknowledge, contain, report to NCMEC, document. Every action is stamped with who, when, and a note, so the trail is defensible months later.' },
    manual: 'Decisions scattered across email and memory; no consistent process, no reliable record.',
    ai: 'A guided queue with a fixed runbook and an immutable log. Consistent handling, complete history, one query away.',
    tags: ['Accountability', 'SLA tracking', 'Auditability'],
  },
  {
    icon: <ExternalIcon />,
    title: 'Illegal content is escalated externally',
    narration: 'For child-safety material, the law requires a report to NCMEC. The reviewer files it and records the reference against the case.',
    ux: (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>3. Report to NCMEC</Typography>
        <Typography variant="caption" sx={{ color: '#65726f', display: 'block', mb: 1.5 }}>
          Percoro never stores the material &mdash; only a content reference. Report it to NCMEC and let them retrieve it.
        </Typography>
        <Button variant="outlined" size="small" endIcon={<ExternalIcon fontSize="small" />}
          href={NCMEC_URL} target="_blank" rel="noopener noreferrer"
          sx={{ textTransform: 'none', color: TEAL, borderColor: TEAL, mb: 1.5 }}>
          NCMEC CyberTipline
        </Button>
        <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: '#fbfaf7', border: '1px dashed #d8d2c4' }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: INK }}>NCMEC report #: ████-██████ &middot; filed</Typography>
        </Box>
      </Paper>
    ),
    rigor: { title: 'A legal duty, provably met', body: 'Some content carries a statutory duty to report to NCMEC within a fixed window. The external reference and timestamp are recorded against the case, so the obligation is demonstrably satisfied.' },
    manual: 'Someone must know the duty exists, know the deadline, file it, and keep the confirmation \u2014 all on memory.',
    ai: 'The runbook surfaces the obligation and captures the reference against the case. Nothing is dropped.',
    tags: ['\u00a72258A', 'Regulatory deadline'],
  },
];

const OBLIGATION_EXAMPLES = ['GDPR', 'COPPA', '\u00a72258A', 'EU DSA', 'HIPAA', 'SOC 2', 'SLAs'];

const TrustSafetyDemo: React.FC<{ variant?: Variant }> = ({ variant: variantProp }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const variant: Variant = useMemo(() => {
    if (variantProp) return variantProp;
    return new URLSearchParams(location.search).get('v') === 'advisory' ? 'advisory' : 'percoro';
  }, [variantProp, location.search]);
  const isAdvisory = variant === 'advisory';

  const firstStep = isAdvisory ? -1 : 0;
  const outroStep = STEPS.length;
  const [step, setStep] = useState<number>(firstStep);

  const onIntro = isAdvisory && step === -1;
  const onOutro = step === outroStep;
  const onFlow = step >= 0 && step < STEPS.length;
  const current = onFlow ? STEPS[step] : null;
  const totalFlow = STEPS.length;
  const progress = onFlow ? ((step + 1) / totalFlow) * 100 : onOutro ? 100 : 0;

  const next = () => setStep((s) => Math.min(s + 1, outroStep));
  const back = () => setStep((s) => Math.max(s - 1, firstStep));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f6f8f7', pb: 6 }}>
      <Box sx={{ bgcolor: INK, color: '#fff', px: { xs: 2, md: 4 }, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <ShieldIcon sx={{ color: TEAL }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.1 }}>Trust &amp; Safety, end to end</Typography>
            <Typography variant="caption" sx={{ opacity: 0.65 }}>
              {isAdvisory ? 'AI embedded in a compliance workflow' : 'How Percoro handles a report'}
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={() => navigate('/demo')} sx={{ color: '#fff' }} aria-label="Close"><CloseIcon /></IconButton>
      </Box>

      {current && (
        <Box sx={{ bgcolor: PURPLE, color: '#fff', px: { xs: 2, md: 4 }, py: 2 }}>
          <Typography variant="body1" sx={{ maxWidth: 900, mx: 'auto' }}>{current.narration}</Typography>
        </Box>
      )}

      {onFlow && (
        <Box sx={{ px: { xs: 2, md: 4 }, pt: 2, maxWidth: 1100, mx: 'auto' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#65726f', fontWeight: 700, letterSpacing: '0.08em' }}>
              STEP {String(step + 1).padStart(2, '0')} / {String(totalFlow).padStart(2, '0')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#65726f' }}>{current?.title}</Typography>
          </Stack>
          <LinearProgress variant="determinate" value={progress}
            sx={{ height: 6, borderRadius: 3, bgcolor: '#e3e8e7', '& .MuiLinearProgress-bar': { bgcolor: TEAL } }} />
        </Box>
      )}

      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, md: 4 }, pt: 3 }}>
        {onIntro && (
          <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, border: '1px solid #e3e8e7' }}>
            <Typography variant="h4" fontWeight={800} sx={{ color: INK, mb: 1.5 }}>
              Every business has obligations. Most track them by hand.
            </Typography>
            <Typography variant="body1" sx={{ color: '#4a5654', mb: 2.5, maxWidth: 680 }}>
              Embed AI into the workflow and the system enforces the rule, the human makes the call, and nothing slips.
              Here's one working example &mdash; a trust-&amp;-safety pipeline, end to end.
            </Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
              {OBLIGATION_EXAMPLES.map((o) => <Chip key={o} label={o} variant="outlined" sx={{ borderColor: '#cdd6d4', color: '#4a5654' }} />)}
            </Stack>
            <Typography variant="caption" sx={{ color: '#9aa6a4' }}>Examples only &mdash; yours will differ.</Typography>
            <Box><Button onClick={next} variant="contained" endIcon={<NextIcon />}
              sx={{ mt: 3, bgcolor: TEAL, textTransform: 'none', px: 3, '&:hover': { bgcolor: TEAL_DARK } }}>
              Walk the example
            </Button></Box>
          </Paper>
        )}

        {current && (
          <Box>
            {isAdvisory && step === 0 && (
              <Alert icon={<SparkIcon fontSize="small" />} severity="info" sx={{ mb: 2, '& .MuiAlert-message': { fontSize: '0.82rem' } }}>
                Example of functions built within <strong>Percoro</strong> &mdash; an AI-native product built by a non-developer.
              </Alert>
            )}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'rgba(45,110,106,0.1)', color: TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{current.icon}</Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: INK }}>{current.title}</Typography>
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e3e8e7', bgcolor: '#fff' }}>
                <Typography variant="caption" sx={{ color: '#9aa6a4', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>What people see</Typography>
                <Box sx={{ mt: 1.5 }}>{current.ux}</Box>
              </Paper>

              <Stack spacing={2}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid ' + TEAL, bgcolor: 'rgba(45,110,106,0.04)' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                    <ShieldIcon sx={{ color: TEAL }} fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: TEAL_DARK }}>{current.rigor.title}</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: '#3c4846' }}>{current.rigor.body}</Typography>
                </Paper>

                {isAdvisory && (
                  <Box sx={{ display: 'grid', gap: 1.5 }}>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #ead9b8', bgcolor: '#fdfaf2' }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <ManualIcon sx={{ color: '#b07d2b' }} fontSize="small" />
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#8a6420', textTransform: 'uppercase' }}>Manual</Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ color: '#5c5238' }}>{current.manual}</Typography>
                    </Paper>
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid ' + TEAL, bgcolor: 'rgba(45,110,106,0.06)' }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                        <AIIcon sx={{ color: TEAL }} fontSize="small" />
                        <Typography variant="caption" fontWeight={700} sx={{ color: TEAL_DARK, textTransform: 'uppercase' }}>With AI</Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ color: '#2f3b39' }}>{current.ai}</Typography>
                    </Paper>
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      {current.tags.map((t) => <Chip key={t} size="small" label={t} sx={{ bgcolor: INK, color: '#fff', fontWeight: 600 }} />)}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
        )}

        {onOutro && !isAdvisory && (
          <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, border: '1px solid ' + TEAL, bgcolor: 'rgba(45,110,106,0.04)', textAlign: 'center' }}>
            <DoneIcon sx={{ color: TEAL, fontSize: 44, mb: 1 }} />
            <Typography variant="h4" fontWeight={800} sx={{ color: INK, mb: 1.5 }}>We keep your safety top of mind.</Typography>
            <Typography variant="body1" sx={{ color: '#4a5654', mb: 3, maxWidth: 560, mx: 'auto' }}>
              From the first tap to the final record, every report is handled with care, speed, and a trail you can trust. Join Percoro.
            </Typography>
            <Button onClick={() => { window.location.href = '/demo?screen=outro'; }} variant="contained"
              sx={{ bgcolor: TEAL, textTransform: 'none', px: 4, py: 1.25, fontSize: '1rem', '&:hover': { bgcolor: TEAL_DARK } }}>
              Finish
            </Button>
          </Paper>
        )}

        {onOutro && isAdvisory && (
          <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, border: '1px solid ' + TEAL, bgcolor: 'rgba(45,110,106,0.04)' }}>
            <Typography variant="h4" fontWeight={800} sx={{ color: INK, mb: 1.5 }}>Yours will look different.</Typography>
            <Typography variant="body1" sx={{ color: '#4a5654', mb: 3, maxWidth: 620 }}>
              We map your obligations, design the workflow, and embed AI so compliance is automatic and provable &mdash; built on real, working software.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button href={PERCORO_URL} target="_blank" rel="noopener noreferrer" variant="outlined" endIcon={<ExternalIcon />}
                sx={{ textTransform: 'none', color: TEAL, borderColor: TEAL }}>
                See the live environment
              </Button>
              <Button href={CHAADV_CONTACT} target="_blank" rel="noopener noreferrer" variant="contained" endIcon={<NextIcon />}
                sx={{ bgcolor: TEAL, textTransform: 'none', '&:hover': { bgcolor: TEAL_DARK } }}>
                Request an advisory session
              </Button>
            </Stack>
          </Paper>
        )}

        <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
          <Button onClick={back} disabled={step === firstStep} startIcon={<BackIcon />} sx={{ textTransform: 'none', color: '#65726f' }}>Back</Button>
          {step < outroStep ? (
            <Button onClick={next} variant="contained" endIcon={<NextIcon />}
              sx={{ bgcolor: TEAL, textTransform: 'none', px: 3, '&:hover': { bgcolor: TEAL_DARK } }}>
              {onIntro ? 'Start' : 'Next'}
            </Button>
          ) : (
            <Button onClick={() => setStep(firstStep)} sx={{ textTransform: 'none', color: '#65726f' }}>Replay</Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default TrustSafetyDemo;
